"use client";
import {CSSProperties, JSX, RefObject, useEffect, useRef, useState} from "react";
import {exo2SemiBold, ralewayBold, montserratRegular, exo2Bold} from "@@/utils/fonts";
import useAppearOnScroll from "@@/hooks/useAppearsOnScroll";
import ProgressiveImage from "@@/utils/ProgressiveImage";
import Image, {StaticImageData} from "next/image";
import styles from "@@/styles/components/shared/CardList.module.scss";
import Button from "@@/components/ui/Button";
import Link from "next/link";


interface Props {
    title: string;
    subtitle?: string;
    cardClassName?: string;
    cards: {
        title: string;
        text: string;
        link?: string;
        button?: {
            text: string;
            link: string;
        };
        alt?: string;
        animation?: {
            ref: RefObject<HTMLLIElement | null>;
            className: string;
            style: CSSProperties;
        }[];
    }[];
    ctaStyle?: {
        bgColor: string;
        textColor: string;
        className: string;
    }
    images?: StaticImageData[];
    images4k?: StaticImageData[];
    icons?: {
        src: any;
        alt: string;
    }[];

    sectionId?: string;
    className?: string;
    titleCol?:boolean;
}

export default function CardList({ title, subtitle, cards, sectionId, images, images4k, icons, className, titleCol, cardClassName, ctaStyle }: Props): JSX.Element {

    const [activeIndex, setActiveIndex] = useState(0);

    const titleAnim = useAppearOnScroll<HTMLHeadingElement>({ from: "fromBottom", duration: 800, delay: 0 });
    const subTitleAnim = useAppearOnScroll<HTMLHeadingElement>({ from: "fromBottom", duration: 800, delay: 200});

    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const sectionRef = useRef<HTMLElement | null>(null);

    // On ajoute dynamiquement les animations, sans muter les props
    const animatedItems = Array.isArray(cards)
        ? cards.map((item, index) => {
            const animation = useAppearOnScroll<HTMLLIElement>({
                from: "fromBottom",
                duration: 500,
                delay: index * 100,
                offset: 200
            });
            return { ...item, animation };
        }) : [];

    const animatedContent = useAppearOnScroll<HTMLElement>({
        from: "fromRight",
        duration: 800,
        delay: 0
    })

    useEffect(() => {}, [animatedItems.length]);


    return (
        <section id={sectionId} ref={sectionRef} className={`px-11 position-relative color-dark ${className}`}>
            <div className="w-100 p-5 d-flex flex-column gap-5 rounded-1 overflow-hidden p-2-mobile">
                <h2
                    ref={titleAnim.ref}
                    className={`${exo2Bold.className} ${titleAnim.className}`}
                    style={titleAnim.style}
                    dangerouslySetInnerHTML={{__html: title}}
                >
                </h2>
                {subtitle && (
                    <h3 ref={subTitleAnim.ref}
                        className={` ${exo2SemiBold.className} ${subTitleAnim.className}`}
                        style={subTitleAnim.style}>
                        {subtitle}
                    </h3>
                )}
                {/* List de cards */}
                <ul
                    className={`${styles.cardListContainer} list-none`}
                >
                    {animatedItems.map((item, index) => (
                        <li
                            key={index}
                            ref={item.animation.ref}
                            className={`duration-500 ease-in-out mx-height-100 ${styles.cardContainer} ${images?.[index] && (styles.hasImage)} ${item.animation.className} ${titleCol ? 'min-w-200 p-0' : ''} ${cardClassName}`}
                            style={item.animation.style}
                        >
                            {images?.[index] && images4k?.[index] && (
                                <ProgressiveImage
                                    alt={item.alt || ''}
                                    width={images4k[index].width}
                                    height={images4k[index].height}
                                    lowSrc={images[index]}
                                    highSrc= {images4k[index]}
                                    quality={58}
                                    classLowRes={`df`}
                                    noRef={true}
                                    className={`position-relative mx-height-100 h-100-with-img overflow-hidden rounded-1 flex-1 w-100`}
                                />
                            )}
                            <div className={`d-flex flex-column gap-1 space-between ${styles.cardContent}`}>

                                <div className={`${styles.cardTitleContainer} ${titleCol ? 'd-flex flex-column' : ''}`}>
                                    {icons?.[index] && (
                                        <Image src={icons[index].src} alt={icons[index].alt} className={`${styles.cardIcon}`}/>
                                    )}
                                    {subtitle ? (
                                        <h4 className={`${exo2SemiBold.className} ${styles.cardTitle}`}>{item.title}</h4>
                                        ) : (
                                        <h3 className={`${exo2SemiBold.className} ${styles.cardTitle}`}>{item.title}</h3>
                                    )}
                                </div>
                                <p className={`${montserratRegular.className} ${styles.cardText}`} dangerouslySetInnerHTML={{__html: item.text}}></p>

                                {item.button && (
                                    <Button href={item.button.link} title={item.button.text} bgColor={ctaStyle?.bgColor || "var(--grey-000)"} textColor={ctaStyle?.textColor || "var(--blue-800)"} className={`secondaryBtn ${ctaStyle?.className || ''}`}/>
                                )}
                                {images?.[index] && !cards[index].button && (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="29" height="30" viewBox="0 0 29 30" fill="none">
                                        <path d="M0 1H28M28 1V29.5M28 1L1 28" stroke="#7DCDF2" strokeWidth="2"/>
                                    </svg>
                                )}
                            </div>
                            {item.link && (
                                <Link href={item.link} className={`position-absolute z-99 w-100 h-100`}/>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
}